/**
 * State for the contract import wizard (`/import/contract`).
 *
 * `analyze` uploads the PDF, lets the backend extract the structured contract data and
 * seeds per-section decisions (link/create + editable fields) against live customers /
 * objects / services pulled via the existing domain stores. The steps mutate those
 * decisions; `commit` serializes them into the `ContractImportCommitRequest` JSON and
 * posts it together with the retained PDF in one transactional call.
 */

import { create } from 'zustand';
import { getContractImport } from '@/api/generated/contract-import/contract-import';
import { parseAxiosError } from '@/errors';
import { buildCommitPayload, validateCommitInput, type CommitIssue } from '@/pages/Import/Contract/logic/buildPayload';
import { buildDefaultDecisions, contactPersonNameOf } from '@/pages/Import/Contract/logic/mapExtraction';
import type {
  CommitResponse,
  ContactDecision,
  CustomerDecision,
  ExtractedContract,
  LiveData,
  MatchInfo,
  ObjectDecision,
  OfferDecision,
  PositionDecision,
} from '@/pages/Import/Contract/logic/types';
import type { LiveCustomer, LiveObject } from '@/pages/Import/logic/types';
import { useCustomerStore } from './useCustomerStore';
import { useObjectStore } from './useObjectStore';
import { useServiceStore } from './useServiceStore';

export type ContractWizardStep = 'upload' | 'customer' | 'object' | 'positions' | 'summary' | 'result';
export const CONTRACT_WIZARD_STEPS: ContractWizardStep[] = [
  'upload', 'customer', 'object', 'positions', 'summary', 'result',
];

const contractImportApi = getContractImport();

const EMPTY_LIVE: LiveData = { customers: [], objects: [], services: [] };
const EMPTY_CUSTOMER: CustomerDecision = {
  mode: 'create', existingCustomerId: '', name: '', street: '', zip: '', city: '', country: '', vatId: '',
};
const EMPTY_CONTACT: ContactDecision = {
  enabled: false, firstName: '', lastName: '', role: '', email: '', phone: '',
};
const EMPTY_OBJECT: ObjectDecision = {
  mode: 'create', existingObjectId: '', name: '', street: '', zip: '', city: '', country: '',
};
const EMPTY_OFFER: OfferDecision = {
  offerDate: '', taxRate: 20, contactPersonName: '', introductionText: '', closingText: '', paymentTerms: '',
};
const NO_MATCH: MatchInfo<never> = { category: 'NEU', score: 0, suggestion: null };

async function loadLiveData(): Promise<LiveData> {
  const customerStore = useCustomerStore.getState();
  const objectStore = useObjectStore.getState();
  const serviceStore = useServiceStore.getState();
  await Promise.all([
    customerStore.fetchAllCustomers(),
    objectStore.fetchAllObjects(),
    serviceStore.fetchServices(),
  ]);
  const customers: LiveCustomer[] = useCustomerStore.getState().customers.map((c) => ({
    id: c.id ?? '',
    name: c.name ?? '',
    customerNumber: c.customerNumber ?? '',
  }));
  const objects: LiveObject[] = useObjectStore.getState().objects.map((o) => ({
    id: (o.id as string) ?? '',
    customerId: (o.customerId as string) ?? '',
    name: (o.name as string) ?? '',
    street: ((o.address as { street?: string } | undefined)?.street) ?? '',
  }));
  const services = useServiceStore.getState().services.map((s) => ({
    id: s.id ?? '',
    name: s.name ?? '',
    category: s.category ?? '',
  }));
  return { customers, objects, services };
}

/** Store errors are i18n keys (`errors` namespace), per the store convention. */
function errorKey(error: unknown): string {
  const apiError = parseAxiosError(error);
  return `errors:${apiError.code || 'common.internal'}`;
}

interface ContractImportState {
  step: ContractWizardStep;
  file: File | null;
  fileName: string | null;
  extraction: ExtractedContract | null;
  analyzing: boolean;
  committing: boolean;
  error: string | null;
  live: LiveData;

  customer: CustomerDecision;
  customerMatch: MatchInfo<LiveCustomer>;
  contact: ContactDecision;
  object: ObjectDecision;
  objectMatch: MatchInfo<LiveObject>;
  positions: PositionDecision[];
  offer: OfferDecision;
  result: CommitResponse | null;

  setStep: (step: ContractWizardStep) => void;
  analyze: (file: File) => Promise<void>;
  commit: () => Promise<void>;
  reset: () => void;
  clearError: () => void;

  setCustomer: (patch: Partial<CustomerDecision>) => void;
  setContact: (patch: Partial<ContactDecision>) => void;
  setObject: (patch: Partial<ObjectDecision>) => void;
  setPosition: (key: string, patch: Partial<PositionDecision>) => void;
  setOffer: (patch: Partial<OfferDecision>) => void;

  commitIssues: () => CommitIssue[];
}

export const useContractImportStore = create<ContractImportState>((set, get) => ({
  step: 'upload',
  file: null,
  fileName: null,
  extraction: null,
  analyzing: false,
  committing: false,
  error: null,
  live: EMPTY_LIVE,

  customer: EMPTY_CUSTOMER,
  customerMatch: NO_MATCH,
  contact: EMPTY_CONTACT,
  object: EMPTY_OBJECT,
  objectMatch: NO_MATCH,
  positions: [],
  offer: EMPTY_OFFER,
  result: null,

  setStep: (step) => set({ step }),

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      step: 'upload', file: null, fileName: null, extraction: null, analyzing: false,
      committing: false, error: null, live: EMPTY_LIVE,
      customer: EMPTY_CUSTOMER, customerMatch: NO_MATCH, contact: EMPTY_CONTACT,
      object: EMPTY_OBJECT, objectMatch: NO_MATCH, positions: [], offer: EMPTY_OFFER,
      result: null,
    }),

  analyze: async (file) => {
    set({ analyzing: true, error: null, file, fileName: file.name });
    try {
      const [response, live] = await Promise.all([
        contractImportApi.postApiV1ContractImportAnalyze({ file }),
        loadLiveData(),
      ]);
      const extraction = response.extraction ?? {};
      const defaults = buildDefaultDecisions(extraction, live);
      set({
        extraction,
        fileName: response.fileName ?? file.name,
        live,
        customer: defaults.customer,
        customerMatch: defaults.customerMatch,
        contact: defaults.contact,
        object: defaults.object,
        objectMatch: defaults.objectMatch,
        positions: defaults.positions,
        offer: defaults.offer,
        analyzing: false,
        step: 'customer',
      });
    } catch (error) {
      set({ analyzing: false, error: errorKey(error) });
    }
  },

  commit: async () => {
    const { file, customer, contact, object, positions, offer } = get();
    if (!file) return;
    const input = { customer, contact, object, positions, offer };
    if (validateCommitInput(input).length > 0) return;

    set({ committing: true, error: null });
    try {
      const payload = buildCommitPayload(input);
      const result = await contractImportApi.postApiV1ContractImportCommit({
        file,
        payload: JSON.stringify(payload),
      });
      set({ result, committing: false, step: 'result' });
    } catch (error) {
      set({ committing: false, error: errorKey(error) });
    }
  },

  setCustomer: (patch) => set((s) => ({ customer: { ...s.customer, ...patch } })),
  // The offer's contact person follows the contact so edits made in the wizard reach the
  // offer; empty (contact disabled) lets the BE fall back to the customer name.
  setContact: (patch) =>
    set((s) => {
      const contact = { ...s.contact, ...patch };
      return { contact, offer: { ...s.offer, contactPersonName: contactPersonNameOf(contact) } };
    }),
  setObject: (patch) => set((s) => ({ object: { ...s.object, ...patch } })),
  setPosition: (key, patch) =>
    set((s) => ({
      positions: s.positions.map((p) => (p.key === key ? { ...p, ...patch } : p)),
    })),
  setOffer: (patch) => set((s) => ({ offer: { ...s.offer, ...patch } })),

  commitIssues: () => {
    const { customer, contact, object, positions, offer } = get();
    return validateCommitInput({ customer, contact, object, positions, offer });
  },
}));

export default useContractImportStore;
